import { ShiftCounts, ShiftTypeWithTime, TimeObject } from "./postShift.types";
import { ShiftType } from "./postShift.schema";
import { CreateShiftBatchCreateJobDto } from "@clipboard-health/contract-backend-main";
import {
  addDays,
  addHours,
  addMinutes,
  differenceInHours,
  differenceInMinutes,
  eachDayOfInterval,
  format,
  isAfter,
  isBefore,
  parseISO,
  set,
  setHours,
  setMinutes,
} from "date-fns";
import { formatInTimeZone, zonedTimeToUtc } from "date-fns-tz";
import { DefaultShiftTimes } from "src/containers/facilityDashboard/ShiftCalendar/api";
import { getFlooredHourMinuteDuration } from "../facilityDashboard/ShiftCalendar/newShift/shiftDate";
import { RestrictedShiftCreationError } from "./errors";
import { isDefined } from "@clipboard-health/util-ts";
import { AxiosError } from "axios";
import { z } from "zod";
import moment from "moment-timezone";
import type { ShiftScheduleDo } from "@src/appV2/ShiftSchedule/api/useCreateShiftSchedule";
import { isShiftAfterLeadTime } from "@src/appV2/InstantFillShifts/utils";

// deviation from standard AM/PM/NOC start time in hours
export const ALLOWED_DEVIATION = 6;

/**
 * Parse a date key string (YYYY-MM-DD format) into a Date object in local time.
 * This avoids timezone issues that occur with new Date(dateString).
 */
export function parseDateKey(dateKey: string): Date {
  const [year, month, day] = dateKey.split("-").map(Number);
  return new Date(year, month - 1, day); // month is 0-indexed
}

export function getShiftTypeTimes(shiftTime: ShiftTypeWithTime) {
  const startTime = setMinutes(setHours(new Date(), shiftTime.start.hour), shiftTime.start.minute);
  const endTime = addMinutes(
    addHours(startTime, shiftTime.duration.hours),
    shiftTime.duration.minutes
  );

  return { startTime, endTime };
}

export function getShiftSubmitText(count: number): string {
  const prefix = +count === 1 ? "shift" : "shifts";
  return `${count} ${prefix}`;
}

export function calculateDuration(start: Date | null, end: Date | null) {
  if (start && end) {
    // Make sure the start and end are on the same day
    const endWithStartDay = set(end, {
      year: start.getFullYear(),
      month: start.getMonth(),
      date: start.getDate(),
    });

    const adjustedEnd = isBefore(endWithStartDay, start)
      ? addDays(endWithStartDay, 1)
      : endWithStartDay;
    const minutes = differenceInMinutes(adjustedEnd, start);

    return getFlooredHourMinuteDuration(minutes);
  }
}

export function calculateDeviation(
  start: Date | null,
  end: Date | null,
  shiftType: ShiftType,
  shiftTimes?: DefaultShiftTimes
) {
  if (start && end && shiftTimes) {
    const { startTime } = getShiftTypeTimes(shiftTimes[shiftType]);
    const hours = Math.abs(differenceInHours(startTime, start));
    if (hours > 12) {
      return 24 - hours;
    } else {
      return hours;
    }
  } else {
    return 0;
  }
}

export function formatTime(time?: TimeObject) {
  if (!time) {
    return "";
  }
  const { hours, minutes } = time;
  const timeParts: string[] = [];

  if (hours > 0) {
    const formattedHours = String(hours).replace(/^0+/, "");
    const hourString = `${formattedHours} hr${hours !== 1 ? "s" : ""}`;
    timeParts.push(hourString);
  }

  if (minutes > 0) {
    const formattedMinutes = String(minutes).replace(/^0+/, "");
    const minuteString = `${formattedMinutes} min${minutes !== 1 ? "s" : ""}`;
    timeParts.push(minuteString);
  }

  return timeParts.join(" ");
}

/**
 * Determines the correct calendar dates for shift start and end times.
 * Handles overnight shifts where end time is earlier than start time.
 *
 * @param day - The calendar date for the shift start
 * @param startTime - Time-only Date object (only hour/minute used)
 * @param endTime - Time-only Date object (only hour/minute used)
 * @returns Object with start and end date strings in "yyyy-MM-dd" format
 */
export function getShiftDates(
  day: Date,
  startTime: Date,
  endTime: Date
): { startDate: string; endDate: string } {
  const startDate = format(day, "yyyy-MM-dd");
  const startTimeString = format(startTime, "HH:mm");
  const endTimeString = format(endTime, "HH:mm");

  // Detect overnight shift by comparing time strings (lexicographic comparison works for HH:mm)
  const isOvernight = endTimeString < startTimeString;

  // If overnight, end date is next calendar day
  const endDate = isOvernight ? format(addDays(parseISO(startDate), 1), "yyyy-MM-dd") : startDate;

  return { startDate, endDate };
}

/**
 * Converts a date string and time string to a UTC timestamp in the specified timezone.
 * Preserves wall-clock time during DST transitions.
 *
 * @param dateString - Date in "yyyy-MM-dd" format
 * @param timeString - Time in "HH:mm" format
 * @param timezone - IANA timezone identifier (e.g., "America/Los_Angeles")
 * @returns UTC Date object representing the timestamp
 */
export function buildTimestamp(dateString: string, timeString: string, timezone: string): Date {
  return zonedTimeToUtc(`${dateString}T${timeString}`, timezone);
}

/**
 * Calculates shift start and end timestamps in UTC, accounting for timezone and overnight shifts.
 *
 * @param day - The calendar date for the shift start (Date object, typically in local browser timezone)
 * @param startTime - Time-only Date object representing shift start time (only hour/minute used)
 * @param endTime - Time-only Date object representing shift end time (only hour/minute used)
 * @param timezone - IANA timezone identifier (e.g., "America/Los_Angeles", "America/New_York")
 * @returns Object with `startAt` and `endAt` as UTC Date objects representing the shift times
 */
export function getShiftTimes(
  day: Date,
  startTime: Date,
  endTime: Date,
  timezone: string
): { startAt: Date; endAt: Date } {
  const { startDate, endDate } = getShiftDates(day, startTime, endTime);
  const startTimeString = format(startTime, "HH:mm");
  const endTimeString = format(endTime, "HH:mm");

  const startAt = buildTimestamp(startDate, startTimeString, timezone);
  const endAt = buildTimestamp(endDate, endTimeString, timezone);

  return { startAt, endAt };
}

export function isShiftStartWithTzBeforeNow(startDate: Date, startTime: Date | null, tmz: string) {
  if (!startTime) {
    return false;
  }

  const startDateInUtc = zonedTimeToUtc(
    `${format(startDate, "yyyy-MM-dd")}T${format(startTime, "HH:mm")}`,
    tmz
  );
  const startDateWithTz = new Date(
    formatInTimeZone(startDateInUtc, tmz, "yyyy-MM-dd'T'HH:mm:00XXX")
  );
  const nowWithTz = new Date(formatInTimeZone(new Date(), tmz, "yyyy-MM-dd'T'HH:mm:00XXX"));
  return isBefore(startDateWithTz, nowWithTz);
}

export function getDatesInRange(
  start: Date | null,
  end: Date | null | undefined,
  daysOfWeek: string[]
): Date[] {
  const dates: Date[] = [];
  if (!start || !end || isAfter(start, end)) {
    return dates;
  }
  const interval = { start, end };
  eachDayOfInterval(interval).forEach((currentDate) => {
    if (daysOfWeek.includes(format(currentDate, "e"))) {
      dates.push(currentDate);
    }
  });
  return dates;
}

export function checkShiftCreationError(error: unknown, type: RestrictedShiftCreationError) {
  return error?.["response"]?.["body"]?.["error"] === type;
}

const newErrorsSchema = z.array(
  z.object({
    status: z.string(),
    code: z.string(),
    title: z.string(),
    detail: z.string().optional(),
  })
);

export function extractErrorFromNewApiResponse(error: unknown) {
  if (!(error instanceof AxiosError) || !error.response || !("errors" in error.response.data)) {
    return undefined;
  }

  const parseResult = newErrorsSchema.safeParse(error.response.data.errors);

  return parseResult.success ? parseResult.data : undefined;
}

export function extractErrorFromApiResponse(responseBody?: unknown) {
  if (
    !responseBody ||
    typeof responseBody !== "object" ||
    !("errors" in responseBody) ||
    !Array.isArray(responseBody.errors)
  ) {
    return undefined;
  }

  const errorList = responseBody.errors
    .map((error) => {
      if (!error.status || !error.detail) {
        return undefined;
      }

      return { status: error.status, detail: error.detail };
    })
    .filter((item) => isDefined(item));

  if (errorList.length === 0) {
    return undefined;
  }

  return errorList;
}

// Inform the users that shifts with rush fee were created, example message:
//  2 x PM shift on 7 Jan, 2025
export function getRushFeeMessages(bulkShiftResponse: ShiftScheduleDo): string[] {
  const { shifts, schedule } = bulkShiftResponse;
  const { timeSlot } = schedule;

  // shifts with rush fee grouped by their startAt
  const shiftsWithRushFee = shifts
    .filter((shift) => shift.charges.rushFeeDifferential)
    .reduce(
      (acc, shift) => acc.set(shift.schedule.startAt, (acc.get(shift.schedule.startAt) || 0) + 1),
      new Map<string, number>()
    );

  return Array.from(shiftsWithRushFee.entries()).map(
    ([startAt, total]) => `${total} x ${timeSlot} shift on ${moment(startAt).format("D MMM, YYYY")}`
  );
}

/**
 * Calculate totals for each shift type from shiftCounts
 */
export function getShiftTypeTotals(shiftCounts: Record<string, ShiftCounts>) {
  return Object.values(shiftCounts).reduce(
    (totals, dayCounts) => ({
      [ShiftType.AM]: totals[ShiftType.AM] + dayCounts[ShiftType.AM],
      [ShiftType.PM]: totals[ShiftType.PM] + dayCounts[ShiftType.PM],
      [ShiftType.NOC]: totals[ShiftType.NOC] + dayCounts[ShiftType.NOC],
    }),
    {
      [ShiftType.AM]: 0,
      [ShiftType.PM]: 0,
      [ShiftType.NOC]: 0,
    }
  );
}

/**
 * Get the first and last dates that have any shifts
 */
export function getShiftDateRange(shiftCounts: Record<string, ShiftCounts>) {
  const datesWithShifts = Object.entries(shiftCounts)
    .filter(
      ([_, dayCounts]) =>
        dayCounts[ShiftType.AM] > 0 || dayCounts[ShiftType.PM] > 0 || dayCounts[ShiftType.NOC] > 0
    )
    .map(([dateKey, _]) => parseDateKey(dateKey))
    .sort((a, b) => a.getTime() - b.getTime());

  if (datesWithShifts.length === 0) {
    return null;
  }

  return {
    firstDate: datesWithShifts[0],
    lastDate: datesWithShifts[datesWithShifts.length - 1],
  };
}

/**
 * Calculate total shift count from shift counts object
 */
export function getTotalShiftCount(shiftCounts: Record<string, ShiftCounts>) {
  let totalCount = 0;
  Object.values(shiftCounts).forEach((dayShifts) => {
    totalCount += dayShifts[ShiftType.AM] + dayShifts[ShiftType.PM] + dayShifts[ShiftType.NOC];
  });
  return totalCount;
}

/**
 * Get the first shift date from shift counts using timezone
 */
export function getFirstShiftDate(
  shiftCounts: Record<string, ShiftCounts>,
  timezone: string
): Date {
  return Object.entries(shiftCounts)
    .map(([dateKey]) => {
      const localDate = parseDateKey(dateKey);
      return moment.tz(moment(localDate).format("YYYY-MM-DD"), timezone);
    })
    .reduce((e1, e2) => (e1 < e2 ? e1 : e2));
}

/**
 * Validate that shifts are not scheduled in the past
 */
export function validateShiftDatesNotInPast(
  shiftCounts: Record<string, ShiftCounts>,
  currentShiftTimes: {
    [ShiftType.AM]: { startTime: Date; endTime: Date };
    [ShiftType.PM]: { startTime: Date; endTime: Date };
    [ShiftType.NOC]: { startTime: Date; endTime: Date };
  } | null,
  timezone: string
): boolean {
  return Object.entries(shiftCounts).some(([dateKey, dayCounts]) => {
    const date = parseDateKey(dateKey);

    return Object.entries(dayCounts).some(([shiftType, count]) => {
      if (count > 0 && currentShiftTimes) {
        const { startTime } = currentShiftTimes[shiftType as ShiftType];

        return isShiftStartWithTzBeforeNow(date, startTime, timezone);
      }
      return false;
    });
  });
}

/**
 * Convert calendar shift counts to batch API format
 */
export function convertCalendarShiftsToBatchFormat(
  shiftCounts: Record<string, ShiftCounts>,
  currentShiftTimes: {
    [ShiftType.AM]: { startTime: Date; endTime: Date };
    [ShiftType.PM]: { startTime: Date; endTime: Date };
    [ShiftType.NOC]: { startTime: Date; endTime: Date };
  } | null,
  timezone: string,
  workerType: string,
  workplaceId: string,
  isInstantFillShiftsEnabled: boolean,
  instantFillLeadTimeHours: number,
  facilityHasRequestToBook: boolean,
  isRequestToBookEnabled: boolean
): CreateShiftBatchCreateJobDto {
  const shifts: CreateShiftBatchCreateJobDto["data"]["attributes"]["shifts"] = [];

  Object.entries(shiftCounts).forEach(([dateKey, dayCounts]) => {
    const date = parseDateKey(dateKey);

    // Process each shift type for this day
    Object.entries(dayCounts).forEach(([shiftType, count]) => {
      if (count > 0 && currentShiftTimes) {
        const { startTime, endTime } = currentShiftTimes[shiftType as ShiftType];
        const { startAt, endAt } = getShiftTimes(date, startTime, endTime, timezone);

        shifts.push({
          startAt: startAt.toISOString(),
          endAt: endAt.toISOString(),
          timeSlot: shiftType,
          count,
          requirements: {
            qualificationName: workerType,
          },
          ...(isInstantFillShiftsEnabled && {
            isInstantFill: isShiftAfterLeadTime({
              shiftStartDate: startAt,
              leadTimeHours: instantFillLeadTimeHours,
            }),
          }),
          ...(facilityHasRequestToBook && { isRequestToBook: isRequestToBookEnabled }),
        });
      }
    });
  });

  return {
    data: {
      type: "shift-batch-job",
      attributes: {
        shifts,
        postingSource: "multiple_shifts_calendar",
      },
      relationships: {
        workplace: {
          data: {
            type: "workplace",
            id: workplaceId,
          },
        },
      },
    },
  };
}
